import type { NormalizedSubscriptionTier } from "@/app/subscription";
import {
  normalizeSubscriptionTier,
  SUBSCRIPTION_TIERS,
} from "@/app/subscription";
import { ADMIN_GRANT_PRICE_ID } from "./subscription-tiers";

// The 2026 Folio plan set (T-0611). Single source of truth for the six new Stripe prices: the sellable
// lookup_key list (PLAN_PRICE_LOOKUP_KEYS derives from here), the amounts + intervals the Stripe
// create-script provisions, and the intended monthly credit grant. The tier→coarse mapping is asserted
// against SUBSCRIPTION_TIERS and the amount here matches the two credit maps (TIER_CREDITS,
// PLAN_CREDITS_BY_TIER) — newPricing2026.test.ts guards both so the copies can't drift.
//
// This holds intended prices only as INPUT to the Stripe ops scripts (the create-script, and the T-0654
// yearly reprice + schedule re-point tools, which preflight the live price against these amounts and fail
// closed on drift); the app never reads amounts from here at runtime (display prices come live from Stripe
// via /public/prices). Older dated plans are NOT listed here on purpose — they stay only in the resolver
// maps so existing subscribers keep resolving.
export type NewStripePrice = {
  lookupKey: string;
  tier: Exclude<NormalizedSubscriptionTier, "free">;
  interval: "month" | "year";
  unitAmount: number;
  monthlyCredits: number;
  nickname: string;
  productName: string;
};

export const NEW_STRIPE_PRICES_2026: readonly NewStripePrice[] = [
  {
    lookupKey: "basic_monthly_08_17_2026",
    tier: "basic",
    interval: "month",
    unitAmount: 999,
    monthlyCredits: 600,
    nickname: "basic_monthly_08_17_2026",
    productName: "Folio Solo",
  },
  {
    lookupKey: "basic_yearly_08_17_2026",
    tier: "basic",
    interval: "year",
    unitAmount: 9588,
    monthlyCredits: 600,
    nickname: "basic_yearly_08_17_2026",
    productName: "Folio Solo",
  },
  {
    lookupKey: "standard_monthly_08_17_2026",
    tier: "standard",
    interval: "month",
    unitAmount: 2999,
    monthlyCredits: 1800,
    nickname: "standard_monthly_08_17_2026",
    productName: "Folio Pro",
  },
  {
    lookupKey: "standard_yearly_08_17_2026",
    tier: "standard",
    interval: "year",
    unitAmount: 29988,
    monthlyCredits: 1800,
    nickname: "standard_yearly_08_17_2026",
    productName: "Folio Pro",
  },
  {
    lookupKey: "premium_monthly_08_17_2026", // gitleaks:allow (Stripe plan lookup key, not a secret)
    tier: "premium",
    interval: "month",
    unitAmount: 9999,
    monthlyCredits: 5000,
    nickname: "premium_monthly_08_17_2026",
    productName: "Folio Max",
  },
  {
    lookupKey: "premium_yearly_08_17_2026", // gitleaks:allow (Stripe plan lookup key, not a secret)
    tier: "premium",
    interval: "year",
    unitAmount: 95988,
    monthlyCredits: 5000,
    nickname: "premium_yearly_08_17_2026",
    productName: "Folio Max",
  },
];

export const NEW_PLAN_LOOKUP_KEYS: readonly string[] =
  NEW_STRIPE_PRICES_2026.map((p) => p.lookupKey);

// A manual/admin grant carries a sentinel priceId with no payment processor behind it: the
// admin-grant sentinel ('n/a'), 'nakit' (cash), or 'free'. Such a row is not a sold plan at all, so
// it is never a grandfathered/legacy plan; labelling it "no longer sold" over a $0 price is nonsense.
// Mirrors PLATFORM_BACKFILL_SENTINEL_PRICE_IDS (app/commands/lib/subscriptionPlatformBackfill.ts);
// kept as a local set here because that module reaches into scripts/lib, which must not enter the
// client bundle. Reuses ADMIN_GRANT_PRICE_ID so the 'n/a' sentinel has one source.
export const MANUAL_GRANT_PRICE_IDS: ReadonlySet<string> = new Set([
  ADMIN_GRANT_PRICE_ID,
  "nakit",
  "free",
]);

export function enterpriseLegacySubscriptionTiers(): string[] {
  return Object.keys(SUBSCRIPTION_TIERS).filter(
    (tier) =>
      SUBSCRIPTION_TIERS[tier] === "premium" &&
      !NEW_PLAN_LOOKUP_KEYS.includes(tier),
  );
}

export function isManualGrantPriceId(priceId: string): boolean {
  return MANUAL_GRANT_PRICE_IDS.has(priceId);
}

// A live Stripe subscription whose lookup_key is NOT in the current 2026 catalog is a legacy
// (grandfathered) plan: the customer keeps an older, often higher-priced plan that is no longer
// sold. The subscription panel must NOT reduce it to the nearest current card (that would claim a
// wrong tier and price); it shows the real price and marks the plan as legacy instead.
//
// Currency-independent by design — the lookup_key alone decides, never the amount (a legacy yearly
// standard at $958.80 coincidentally equals today's Max price, so an amount test would misclassify).
// Guarded on `provider === "stripe"`: RevenueCat/iOS tiers (Solo_Yearly, ...) are also outside this
// set but are managed through their own store flows and must never be labelled legacy here. A
// manual/admin grant (sentinel priceId) is excluded too — it is not a sold plan.
export function isLegacyStripeSubscription(input: {
  provider: string | null | undefined;
  tier: string | null | undefined;
  priceId: string | null | undefined;
}): boolean {
  return (
    input.provider === "stripe" &&
    !!input.tier &&
    !NEW_PLAN_LOOKUP_KEYS.includes(input.tier) &&
    !(input.priceId != null && MANUAL_GRANT_PRICE_IDS.has(input.priceId))
  );
}

// Whether the subscription panel marks the plan as legacy. Composes the catalog predicate with the
// two panel-level exclusions so the whole "mark legacy?" decision lives in one pure, tested place:
// a free plan has no legacy state, and a terminal (expired/canceled) plan is not one the customer is
// keeping (the "no longer sold" marking would restate the same contradiction, and it already routes
// to a fresh checkout). (T-0773)
export function shouldMarkLegacyPlan(input: {
  isFreePlan: boolean;
  isTerminal: boolean;
  provider: string | null | undefined;
  tier: string | null | undefined;
  priceId: string | null | undefined;
}): boolean {
  return (
    !input.isFreePlan &&
    !input.isTerminal &&
    isLegacyStripeSubscription({
      provider: input.provider,
      tier: input.tier,
      priceId: input.priceId,
    })
  );
}

// A legacy (grandfathered) Stripe Premium plan is DISPLAYED as "Enterprise" (T-0824). It reuses the
// legacy predicate above and narrows it to the coarse `premium` tier, so the biggest, oldest paying
// customers are told apart from the current 2026 "Folio Max" premium plan, which shares the coarse
// `premium` tier and would otherwise render the identical "Max" label. This is a display-only
// distinction: the raw `tier`, billing, credit grant and the native contract are untouched — only the
// web admin list and the customer current-plan card read it. A legacy basic/standard plan is NOT
// affected (it keeps its own label); only legacy premium becomes "Enterprise".
//
// A manual/admin grant (sentinel priceId) is INTENTIONALLY excluded — it inherits that from
// isLegacyStripeSubscription, and that is the wanted behaviour, not an oversight: an `n/a` grant has
// no payment processor charging it, so it is not one of the "biggest paying" customers this label is
// for (some are internal/dogfood orgs). The genuinely paying legacy premium subs carry a real Stripe
// priceId and are labelled. (T-0824, owner-confirmed.)
//
// Named for its argument: it takes the subscription shape (provider + tier + priceId), like its
// sibling isLegacyStripeSubscription, not a bare tier.
export function isEnterpriseLegacySubscription(input: {
  provider: string | null | undefined;
  tier: string | null | undefined;
  priceId: string | null | undefined;
}): boolean {
  return (
    isLegacyStripeSubscription(input) &&
    normalizeSubscriptionTier(input.tier) === "premium"
  );
}

// Stamped on the subscription schedules the 2026 migration attaches, and checked before ANY release, so
// the migration only ever releases the schedules it created — never a customer's own scheduled change.
export const MIGRATION_SCHEDULE_TAG = "pricing-2026-migration";

// The admin manual-grant surface (AdminController.TIER_MAP fallback + the OrganizationSubscriptionCreate
// dropdown) offers exactly this 2026 set, DERIVED from the source above so it can never drift back onto a
// retired price. Label uses the live product name + cadence (internal admin UI, English like the rest of
// that form). unitAmount stays cents; the app still shows live Stripe prices to customers — this amount is
// only the admin dropdown display and the resolver's Stripe-unreachable fallback. Legacy dated plans are
// deliberately absent: they stay only in the resolver map so existing subscribers keep resolving.
export type StripeAdminGrantOption = {
  lookupKey: string;
  label: string;
  unitAmount: number;
  currency: "USD";
  interval: "month" | "year";
};

export const STRIPE_ADMIN_GRANT_OPTIONS: readonly StripeAdminGrantOption[] =
  NEW_STRIPE_PRICES_2026.map((p) => ({
    lookupKey: p.lookupKey,
    label: `${p.productName} (${p.interval === "month" ? "Monthly" : "Yearly"})`,
    unitAmount: p.unitAmount,
    currency: "USD",
    interval: p.interval,
  }));

// The admin subscription list stores the raw Stripe lookup_key in `Subscription.tier`. This maps the
// six 2026 web keys to a readable "<productName> Web <Monthly|Yearly>" label, derived from the source
// above so it can never drift. Legacy dated keys and the RevenueCat/iOS tiers are deliberately absent.
// A Map (not a plain object) so a tier string that collides with an Object.prototype key
// (`constructor`, `toString`, ...) still misses and returns null, keeping the signature honest.
const WEB_TIER_DISPLAY_NAMES: ReadonlyMap<string, string> = new Map(
  NEW_STRIPE_PRICES_2026.map((p) => [
    p.lookupKey,
    `${p.productName} Web ${p.interval === "month" ? "Monthly" : "Yearly"}`,
  ]),
);

// Readable name for a 2026 web Stripe tier key (used by the admin subscription list and the GA4
// subscription-conversion event), or null for any other tier (legacy Stripe keys, RevenueCat/iOS
// tiers) so the caller keeps showing the raw string.
export function webTierDisplayName(tier: string): string | null {
  return WEB_TIER_DISPLAY_NAMES.get(tier) ?? null;
}

// The label the admin subscription LIST shows for a Stripe subscription's tier: the 2026 web display
// name when it is a current key, else "Enterprise" for a legacy premium, else the raw lookup_key.
// Kept here beside the two predicates it composes so the whole tier-label rule lives in one tested
// place, not inline in the list component. Non-Stripe tiers are the caller's concern. (T-0824)
export function adminSubscriptionTierLabel(input: {
  provider: string | null | undefined;
  tier: string;
  priceId: string | null | undefined;
}): string {
  return (
    webTierDisplayName(input.tier) ??
    (isEnterpriseLegacySubscription(input) ? "Enterprise" : input.tier)
  );
}
